/* eslint-disable @harmony/stencil/no-tailwind-disable */
/* eslint-disable max-len */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React from 'react';
import {
  shape, func, arrayOf, string, number
} from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { Typography, Link } from '@one-thd/sui-atomic-components';
import WasherDryerContainer from './WasherDryerContainer';
import AccessoriesContainer from './AccessoriesContainer';
import { ACCESSORIES, DRYER, WASHER, LAUNDRY_VIEW_BUNDLE } from '../constants/index';
import LaundryProductsLayout from './LaundryProductsLayout';
import { LaundryPrice } from './LaundryPrice';
import { Symbol } from './laundry/Symbol';
import { AlignedContent } from './laundry/AlignedContent';
import { tiggerAnalyticsCustomEvent } from '../utils/product-utils';

/**
* LaundryBundleMiniContent component renders a section for building a matching laundry set.
* It displays a title, a link to shop all laundry sets, container for washer and dryer products,
* container for accessories and bundle price.
*
* @param {Object} props - The props object.
* @param {Object} props.products - An object containing the washer and dryer products.
* @param {Object} props.products.WASHER - The washer product details.
* @param {Object} props.products.DRYER - The dryer product details.
* @param {Object} props.products.ACCESSORIES - The accessory product details.
* @param {Function} props.onSwapVariant - Callback function to handle swapping product variants.
* @param {Object} props.bundle - The bundle object containing pricing details.
* @param {Array} props.selectedProducts - An array of selected products.
*
* @returns {JSX.Element} The rendered LaundryBundleMiniContent component.
*/
export const LaundryBundleMiniContent = ({ products, onSwapVariant: handleSwapVariant, bundle, selectedProducts }) => {
  const bundleAccessories = products[ACCESSORIES];
  const triggerAnalytics = () => {
    const output = {
      itemId: bundle?.itemId,
      product: bundle
    };
    tiggerAnalyticsCustomEvent(LAUNDRY_VIEW_BUNDLE, output);
  };
  return (
    <div className="sui-bg-primary sui-m-4 md:sui-m-7 lg:sui-m-9" data-testid="laundry-entry-point">
      <div className="sui-flex sui-flex-col md:sui-flex-row md:sui-items-center sui-gap-1
      md:sui-gap-0 sui-pt-4 md:sui-pt-6 lg:sui-pt-8 sui-pb-4 sui-justify-between"
      >
        <Typography variant="h2">Build A Matching Set</Typography>
        <Link href="#" target="_blank" variant="body-sm">Shop All Laundry Sets</Link>
      </div>
      <LaundryProductsLayout
        grid={{
          md: 12
        }}
        element="section"
      >
        <div className="md:sui-col-span-7 lg:sui-col-span-5">
          <WasherDryerContainer
            washer={products[WASHER]}
            dryer={products[DRYER]}
            onSwapVariant={handleSwapVariant}
          />
        </div>
        {
          !!bundleAccessories && (
            <>
              <AlignedContent
                wrapperClassName="sui-my-6 md:sui-mb-0 md:sui-mt-10"
              >
                <Symbol symbol="+" />
              </AlignedContent>
              <div className="md:sui-col-span-4 md:sui-col-start-9 lg:sui-col-span-2 lg:sui-col-start-7">
                <AccessoriesContainer product={bundleAccessories} onSwapVariant={handleSwapVariant} />
              </div>
            </>
          )
        }
        <AlignedContent
          wrapperClassName="sui-hidden md:sui-block md:sui-mt-10 md:sui-col-start-1 lg:sui-col-start-auto"
          contentClassName="md:sui-items-start lg:sui-items-center md:sui-justify-start lg:sui-justify-center"
        >
          <Symbol symbol="=" />
        </AlignedContent>
        {
          bundle && selectedProducts && (
            <div className="sui-w-full sui-mt-4 md:sui-mt-6 md:sui-col-span-5 lg:sui-col-span-3 sui-flex sui-flex-col sui-gap-3 lg:sui-mt-[calc(40px_+_18%)]">
              <LaundryPrice bundle={bundle} products={selectedProducts} />
              <span className="sui-self-center">
                <Link
                  href={bundle?.identifiers?.canonicalUrl}
                  target="_blank"
                  color="subtle"
                  underline="always"
                  variant="body-sm"
                  height="tight"
                  onClick={triggerAnalytics}
                >
                  View Set Details
                </Link>
              </span>
            </div>
          )
        }
      </LaundryProductsLayout>
    </div>
  );
};
LaundryBundleMiniContent.propTypes = {
  products: shape({}).isRequired,
  onSwapVariant: func.isRequired,
  bundle: shape({}).isRequired,
  selectedProducts: arrayOf(
    shape({})
  ).isRequired
};
LaundryBundleMiniContent.dataModel = extend(
  WasherDryerContainer,
  AccessoriesContainer,
  LaundryPrice
);